<template>
  <div class="event-markers-overlay">
    <div
      v-if="tooltip.visible && tooltip.event"
      class="event-tooltip-card"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
      @wheel.stop
      @scroll.stop
    >
      <div class="card-header">
        <span class="badge" :class="getEventType(tooltip.event)">
          {{ getBadgeText(tooltip.event) }}
        </span>
        <span class="card-title">{{ getEventTitle(tooltip.event) }}</span>
        <span class="close-btn" @click.stop="tooltip.visible = false">&times;</span>
      </div>

      <div class="card-body">
        <template v-if="getEventType(tooltip.event) === 'dividend'">
          <div class="info-row" v-if="tooltip.event.eventDate || tooltip.event.exDate">
            <span class="label">Ex-dividend date</span>
            <span class="value">{{ formatDate(tooltip.event.eventDate || tooltip.event.exDate) }}</span>
          </div>
          <div class="info-row" v-if="hasValue(getAmount(tooltip.event))">
            <span class="label">Amount</span>
            <span class="value">{{ getAmount(tooltip.event) }}</span>
          </div>
          <div class="info-row" v-if="hasValue(tooltip.event.dividendType)">
            <span class="label">Type</span>
            <span class="value">{{ tooltip.event.dividendType }}</span>
          </div>
          <div class="info-row" v-if="hasValue(tooltip.event.frequency)">
            <span class="label">Frequency</span>
            <span class="value">{{ tooltip.event.frequency }}</span>
          </div>
          <div class="info-row" v-if="tooltip.event.declarationDate">
            <span class="label">Declaration date</span>
            <span class="value">{{ formatDate(tooltip.event.declarationDate) }}</span>
          </div>
          <div class="info-row" v-if="tooltip.event.recordDate">
            <span class="label">Record date</span>
            <span class="value">{{ formatDate(tooltip.event.recordDate) }}</span>
          </div>
          <div class="info-row" v-if="tooltip.event.payDate || tooltip.event.paymentDate">
            <span class="label">Payment date</span>
            <span class="value">{{ formatDate(tooltip.event.payDate || tooltip.event.paymentDate) }}</span>
          </div>
        </template>

        <template v-else-if="getEventType(tooltip.event) === 'split'">
          <div class="info-row" v-if="tooltip.event.eventDate || tooltip.event.executionDate">
            <span class="label">Execution date</span>
            <span class="value">{{ formatDate(tooltip.event.eventDate || tooltip.event.executionDate) }}</span>
          </div>
          <div class="info-row" v-if="getSplitRatio(tooltip.event)">
            <span class="label">Ratio</span>
            <span class="value">{{ getSplitRatio(tooltip.event) }}</span>
          </div>
        </template>

        <template v-else-if="getEventType(tooltip.event) === 'earnings'">
          <div class="earnings-layout">
            <div class="info-row" v-if="tooltip.event.eventDate || tooltip.event.reportDate">
              <span class="label">Date</span>
              <span class="value">{{ formatDate(tooltip.event.eventDate || tooltip.event.reportDate) }}</span>
            </div>
            <div class="info-row" v-if="getPeriodEnding(tooltip.event)">
              <span class="label">Period Ending</span>
              <span class="value">{{ getPeriodEnding(tooltip.event) }}</span>
            </div>

            <div class="section-group" v-if="hasEarningsData(tooltip.event)">
              <div class="section-title">EARNINGS</div>
              <div class="info-row" v-if="hasValue(tooltip.event.standardizedEps || tooltip.event.standardized)">
                <span class="label">Standardized</span>
                <span class="value">{{ formatEps(tooltip.event.standardizedEps || tooltip.event.standardized) }}</span>
              </div>
              <div class="info-row" v-if="hasValue(tooltip.event.epsActual)">
                <span class="label">Reported</span>
                <span class="value">{{ formatEps(tooltip.event.epsActual) }}</span>
              </div>
              <div class="info-row" v-if="hasValue(tooltip.event.epsEstimate)">
                <span class="label">Estimate</span>
                <span class="value">{{ formatEps(tooltip.event.epsEstimate) }}</span>
              </div>
              <div class="info-row" v-if="hasSurprise(tooltip.event, 'eps')">
                <span class="label" :class="getSurpriseClass(tooltip.event, 'eps')">Surprise</span>
                <span class="value" :class="getSurpriseClass(tooltip.event, 'eps')">
                  {{ formatEpsSurprise(tooltip.event) }}
                </span>
              </div>
            </div>

            <div class="section-group" v-if="hasRevenueData(tooltip.event)">
              <div class="section-title">REVENUE</div>
              <div class="info-row" v-if="hasValue(tooltip.event.actualRevenue || tooltip.event.revenueActual)">
                <span class="label">Reported</span>
                <span class="value">{{ formatRevenue(tooltip.event.actualRevenue || tooltip.event.revenueActual) }}</span>
              </div>
              <div class="info-row" v-if="hasValue(tooltip.event.estimatedRevenue || tooltip.event.revenueEstimate)">
                <span class="label">Estimate</span>
                <span class="value">{{ formatRevenue(tooltip.event.estimatedRevenue || tooltip.event.revenueEstimate) }}</span>
              </div>
              <div class="info-row" v-if="hasSurprise(tooltip.event, 'revenue')">
                <span class="label" :class="getSurpriseClass(tooltip.event, 'revenue')">Surprise</span>
                <span class="value" :class="getSurpriseClass(tooltip.event, 'revenue')">
                  {{ formatRevenueSurprise(tooltip.event) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- <div class="card-footer">
        <button class="more-btn">More {{ currentSymbol }} {{ tooltip.event.eventType }}s</button>
      </div> -->
    </div>
  </div>
</template>

<script>
import { Overlay } from 'trading-vue-js'
import moment from 'moment'
import { chartEvents } from '../../chartEvents'

export default {
  name: 'EventMarkers',
  mixins: [Overlay],
  data() {
    return {
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        event: null
      },
      renderedIcons: []
    }
  },
  computed: {
    currentSymbol() {
      return this.$store.state.selectedSymbol || 'AAPL'
    }
  },
  methods: {
    meta_info() {
      return {
        author: 'ProTrader',
        version: '1.0.0',
        desc: 'Corporate Event Markers',
        preset: {
          name: 'EventMarkers',
          side: 'onchart',
          settings: {
            legend: false,
            legendButtons: false
          }
        }
      }
    },
    legend() {
      return []
    },
    use_for() {
      return ['EventMarkers']
    },
    hasValue(val) {
      return val !== undefined && val !== null && val !== ''
    },
    formatRevenue(val) {
      if (!this.hasValue(val)) return ''
      const num = Number(val)
      if (isNaN(num)) return val
      const abs = Math.abs(num)
      if (abs >= 1e12) {
        return parseFloat((num / 1e12).toFixed(2)) + ' T'
      } else if (abs >= 1e9) {
        return parseFloat((num / 1e9).toFixed(2)) + ' B'
      } else if (abs >= 1e6) {
        return parseFloat((num / 1e6).toFixed(2)) + ' M'
      } else if (abs >= 1e3) {
        return parseFloat((num / 1e3).toFixed(2)) + ' K'
      }
      return num.toLocaleString('en-US')
    },
    formatEps(val) {
      if (!this.hasValue(val)) return ''
      const num = Number(val)
      if (isNaN(num)) return val
      return parseFloat(num.toFixed(4)).toString()
    },
    formatTiming(timing) {
      if (!timing) return ''
      const t = timing.toString().toLowerCase().trim()
      if (t === 'bmo') return 'Before Open'
      if (t === 'amc') return 'After Close'
      if (t === 'dmh') return 'During Market'
      return timing.charAt(0).toUpperCase() + timing.slice(1)
    },
    formatPercent(val) {
      if (!this.hasValue(val)) return ''
      const num = Number(val)
      if (isNaN(num)) return val
      const sign = num > 0 ? '+' : ''
      return `${sign}${num.toFixed(2)}%`
    },
    getFiscalPeriod(event) {
      if (!event) return ''
      const parts = []
      if (this.hasValue(event.fiscalPeriod)) parts.push(event.fiscalPeriod)
      if (this.hasValue(event.fiscalYear)) parts.push(event.fiscalYear)
      return parts.join(' ')
    },
    getPeriodEnding(event) {
      if (!event) return ''
      if (this.hasValue(event.periodEnding)) return event.periodEnding
      const period = (event.fiscalPeriod || '').toString().toUpperCase().trim()
      const year = event.fiscalYear ? event.fiscalYear.toString() : ''
      const shortYear = year ? (year.length === 4 ? "'" + year.slice(2) : "'" + year) : ''

      if (period === 'Q1') return shortYear ? `Mar ${shortYear}` : 'Mar'
      if (period === 'Q2') return shortYear ? `Jun ${shortYear}` : 'Jun'
      if (period === 'Q3') return shortYear ? `Sep ${shortYear}` : 'Sep'
      if (period === 'Q4') return shortYear ? `Dec ${shortYear}` : 'Dec'

      return this.getFiscalPeriod(event)
    },
    hasEarningsData(event) {
      if (!event) return false
      return this.hasValue(event.epsActual) ||
             this.hasValue(event.epsEstimate) ||
             this.hasValue(event.epsDifference) ||
             this.hasValue(event.epsDifferencePct) ||
             this.hasValue(event.standardizedEps || event.standardized)
    },
    hasRevenueData(event) {
      if (!event) return false
      return this.hasValue(event.actualRevenue || event.revenueActual) ||
             this.hasValue(event.estimatedRevenue || event.revenueEstimate) ||
             this.hasValue(event.revenueDifference) ||
             this.hasValue(event.revenueDifferencePct)
    },
    hasSurprise(event, type) {
      if (!event) return false
      if (type === 'eps') {
        return this.hasValue(event.epsDifference) ||
               this.hasValue(event.epsDifferencePct) ||
               this.hasValue(event.epsBeatMiss) ||
               (this.hasValue(event.epsActual) && this.hasValue(event.epsEstimate))
      }
      if (type === 'revenue') {
        return this.hasValue(event.revenueDifference) ||
               this.hasValue(event.revenueDifferencePct) ||
               this.hasValue(event.revenueBeatMiss) ||
               (this.hasValue(event.actualRevenue || event.revenueActual) &&
                this.hasValue(event.estimatedRevenue || event.revenueEstimate))
      }
      return false
    },
    formatEpsSurprise(event) {
      if (!event) return ''
      let diff = event.epsDifference
      if (!this.hasValue(diff) && this.hasValue(event.epsActual) && this.hasValue(event.epsEstimate)) {
        diff = Number(event.epsActual) - Number(event.epsEstimate)
      }
      let pct = event.epsDifferencePct
      if (!this.hasValue(pct) && this.hasValue(diff) && this.hasValue(event.epsEstimate) && Number(event.epsEstimate) !== 0) {
        pct = (Number(diff) / Math.abs(Number(event.epsEstimate))) * 100
      }

      const parts = []
      if (this.hasValue(diff)) {
        parts.push(this.formatEps(diff))
      }
      if (this.hasValue(pct)) {
        const pctNum = Number(pct)
        const formattedPct = isNaN(pctNum) ? pct : `${pctNum.toFixed(2)}%`
        parts.push(`(${formattedPct})`)
      }

      return parts.join(' ')
    },
    formatRevenueSurprise(event) {
      if (!event) return ''
      let diff = event.revenueDifference
      const actual = event.actualRevenue !== undefined ? event.actualRevenue : event.revenueActual
      const estimate = event.estimatedRevenue !== undefined ? event.estimatedRevenue : event.revenueEstimate

      if (!this.hasValue(diff) && this.hasValue(actual) && this.hasValue(estimate)) {
        diff = Number(actual) - Number(estimate)
      }
      let pct = event.revenueDifferencePct
      if (!this.hasValue(pct) && this.hasValue(diff) && this.hasValue(estimate) && Number(estimate) !== 0) {
        pct = (Number(diff) / Math.abs(Number(estimate))) * 100
      }

      const parts = []
      if (this.hasValue(diff)) {
        parts.push(this.formatRevenue(diff))
      }
      if (this.hasValue(pct)) {
        const pctNum = Number(pct)
        const formattedPct = isNaN(pctNum) ? pct : `${pctNum.toFixed(2)}%`
        parts.push(`(${formattedPct})`)
      }

      return parts.join(' ')
    },
    getSurpriseClass(event, type) {
      if (!event) return ''
      let beatMiss = ''
      let diff = null
      let pct = null

      if (type === 'eps') {
        beatMiss = (event.epsBeatMiss || '').toString().toLowerCase()
        diff = event.epsDifference
        if (!this.hasValue(diff) && this.hasValue(event.epsActual) && this.hasValue(event.epsEstimate)) {
          diff = Number(event.epsActual) - Number(event.epsEstimate)
        }
        pct = event.epsDifferencePct
      } else if (type === 'revenue') {
        beatMiss = (event.revenueBeatMiss || '').toString().toLowerCase()
        diff = event.revenueDifference
        const actual = event.actualRevenue !== undefined ? event.actualRevenue : event.revenueActual
        const estimate = event.estimatedRevenue !== undefined ? event.estimatedRevenue : event.revenueEstimate
        if (!this.hasValue(diff) && this.hasValue(actual) && this.hasValue(estimate)) {
          diff = Number(actual) - Number(estimate)
        }
        pct = event.revenueDifferencePct
      }

      if (beatMiss === 'beat') return 'text-success'
      if (beatMiss === 'miss') return 'text-danger'

      if (this.hasValue(diff)) {
        if (Number(diff) > 0) return 'text-success'
        if (Number(diff) < 0) return 'text-danger'
      }
      if (this.hasValue(pct)) {
        if (Number(pct) > 0) return 'text-success'
        if (Number(pct) < 0) return 'text-danger'
      }

      return ''
    },
    getBeatMissClass(val) {
      if (!val) return ''
      const lower = val.toString().toLowerCase()
      if (lower === 'beat') return 'text-success'
      if (lower === 'miss') return 'text-danger'
      return ''
    },
    getEventType(event) {
      if (!event) return 'dividend'
      const type = typeof event === 'string' ? event : (event.eventType || event.event_type || event.type || event.category || event.kind || '')
      const lower = type.toLowerCase()
      if (lower.includes('split')) return 'split'
      if (lower.includes('earning')) return 'earnings'
      if (lower.includes('div')) return 'dividend'
      return lower || 'dividend'
    },
    getAmount(event) {
      if (!event) return undefined
      if (event.cashAmount !== undefined && event.cashAmount !== null) return event.cashAmount
      if (event.amount !== undefined && event.amount !== null) return event.amount
      if (event.value !== undefined && event.value !== null) return event.value
      return undefined
    },
    getSplitRatio(event) {
      if (!event) return ''
      if (event.ratio) return event.ratio
      if (event.splitTo !== undefined && event.splitFrom !== undefined) {
        return `${event.splitTo}:${event.splitFrom}`
      }
      return ''
    },
    draw(ctx) {
      this.renderedIcons = []
      const layout = this.$props.layout
      const data = this.$props.data

      if (!data || !data.length) return

      const y = layout.height - 15
      const radius = 11

      data.forEach((item) => {
        // item format: [timestamp, eventObject]
        const timestamp = item[0]
        const event = item[1]

        if (!timestamp || !event) return

        let x
        const evDate = event.eventDate || event.event_date || event.date || event.exDate || event.ex_date || event.executionDate || event.reportDate
        if (evDate && layout && layout.ti_map && typeof layout.ti_map.t2i === 'function') {
          const evTs = new Date(evDate).getTime()
          const idx = layout.ti_map.t2i(evTs)
          x = idx !== undefined ? layout.t2screen(idx) : layout.t2screen(timestamp)
        } else {
          x = layout.t2screen(timestamp)
        }

        if (x < -15 || x > layout.width + 15) return

        ctx.save()
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2, false)

        const evType = this.getEventType(event)
        let color = '#2962FF'
        let text = 'D'

        if (evType === 'split') {
          color = '#7E57C2'
          text = 'S'
        } else if (evType === 'earnings') {
          color = '#089981'
          text = 'E'
        } else if (evType === 'dividend') {
          color = '#2962FF'
          text = 'D'
        } else {
          color = '#FF9800'
          text = evType ? evType.charAt(0).toUpperCase() : 'E'
        }

        ctx.fillStyle = color
        ctx.fill()
        ctx.lineWidth = 1.5
        ctx.strokeStyle = '#FFFFFF'
        ctx.stroke()

        ctx.fillStyle = '#FFFFFF'
        ctx.font = 'bold 11px Inter, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(text, x, y + 1)
        ctx.restore()

        this.renderedIcons.push({ x, y, radius, event })
      })
    },
    getBadgeText(event) {
      const type = this.getEventType(event)
      if (type === 'split') return 'S'
      if (type === 'earnings') return 'E'
      if (type === 'dividend') return 'D'
      return type ? type.charAt(0).toUpperCase() : 'E'
    },
    getEventTitle(event) {
      const type = this.getEventType(event)
      if (type === 'split') return 'Splits'
      if (type === 'earnings') return 'Earnings'
      if (type === 'dividend') return 'Dividends'
      return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Corporate Event'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('ddd DD MMM \'YY')
    },
    handleMouseMove(e) {
      const canvasEl = this.$parent?.$el?.querySelector?.('canvas') || this.$el.parentElement
      if (!this.renderedIcons.length || !canvasEl) return

      const parentRect = canvasEl.getBoundingClientRect()
      const mouseX = e.clientX - parentRect.left
      const mouseY = e.clientY - parentRect.top

      let isOverIcon = false
      for (const icon of this.renderedIcons) {
        const dx = mouseX - icon.x
        const dy = mouseY - icon.y
        if (Math.sqrt(dx * dx + dy * dy) <= icon.radius + 4) {
          isOverIcon = true
          break
        }
      }

      canvasEl.style.cursor = isOverIcon ? 'pointer' : ''
    },
    handleClick(e) {
      const canvasEl = this.$parent?.$el?.querySelector?.('canvas') || this.$el.parentElement
      if (!canvasEl) return

      const parentRect = canvasEl.getBoundingClientRect()
      const mouseX = e.clientX - parentRect.left
      const mouseY = e.clientY - parentRect.top

      let clickedIcon = null
      for (const icon of this.renderedIcons) {
        const dx = mouseX - icon.x
        const dy = mouseY - icon.y
        if (Math.sqrt(dx * dx + dy * dy) <= icon.radius + 4) {
          clickedIcon = icon
          break
        }
      }

      if (clickedIcon) {
        const cardWidth = 240
        let posX = clickedIcon.x - cardWidth / 2
        let posY = clickedIcon.y - clickedIcon.radius - 6

        if (posX < 10) posX = 10

        if (this.tooltip.visible && this.tooltip.event === clickedIcon.event) {
          this.tooltip.visible = false
        } else {
          this.tooltip = {
            visible: true,
            x: posX,
            y: posY,
            event: clickedIcon.event
          }
        }
      } else {
        if (this.tooltip.visible && e.target && !e.target.closest?.('.event-tooltip-card')) {
          this.tooltip.visible = false
        }
      }
    },
    handleScroll(e) {
      if (e && e.target && e.target.closest && e.target.closest('.event-tooltip-card')) {
        return
      }
      if (this.tooltip.visible) {
        this.tooltip.visible = false
      }
    }
  },
  mounted() {
    const parentEl = this.$el.parentElement || document
    parentEl.addEventListener('mousemove', this.handleMouseMove)
    parentEl.addEventListener('click', this.handleClick)
    parentEl.addEventListener('wheel', this.handleScroll, { passive: true })
    window.addEventListener('wheel', this.handleScroll, { passive: true })
    window.addEventListener('scroll', this.handleScroll, { passive: true })

    chartEvents.$on('setChartRange', this.handleScroll)
    chartEvents.$on('reset_chart', this.handleScroll)
  },
  beforeDestroy() {
    const parentEl = this.$el.parentElement || document
    parentEl.removeEventListener('mousemove', this.handleMouseMove)
    parentEl.removeEventListener('click', this.handleClick)
    parentEl.removeEventListener('wheel', this.handleScroll)
    window.removeEventListener('wheel', this.handleScroll)
    window.removeEventListener('scroll', this.handleScroll)

    chartEvents.$off('setChartRange', this.handleScroll)
    chartEvents.$off('reset_chart', this.handleScroll)
  }
}
</script>

<style scoped>
.event-markers-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.event-tooltip-card {
  position: absolute;
  width: 240px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e3eb;
  padding: 12px;
  pointer-events: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  color: #131722;
  z-index: 999;
  transform: translateY(-100%);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f3fa;
}

.close-btn {
  margin-left: auto;
  font-size: 16px;
  font-weight: bold;
  color: #787b86;
  cursor: pointer;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
}

.close-btn:hover {
  color: #131722;
  background: #f0f3fa;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #ffffff;
  font-weight: 700;
  font-size: 11px;
}

.badge.dividend {
  background-color: #2962ff;
}

.badge.split {
  background-color: #7e57c2;
}

.badge.earnings {
  background-color: #089981;
}

.card-title {
  font-weight: 700;
  font-size: 14px;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  color: #787b86;
}

.value {
  font-weight: 600;
  color: #131722;
}

.text-success {
  color: #089981 !important;
}

.text-danger {
  color: #f23645 !important;
}

.earnings-layout {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.earnings-layout .label {
  color: #131722;
  font-weight: 500;
}

.section-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 6px;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #787b86;
  letter-spacing: 0.5px;
  margin-bottom: 1px;
}

.earnings-layout .label.text-success,
.earnings-layout .value.text-success {
  color: #089981 !important;
  font-weight: 600;
}

.earnings-layout .label.text-danger,
.earnings-layout .value.text-danger {
  color: #f23645 !important;
  font-weight: 600;
}

.card-footer {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f3fa;
}

.more-btn {
  width: 100%;
  padding: 6px 10px;
  background: #f0f3fa;
  border: 1px solid #e0e3eb;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #2962ff;
  cursor: pointer;
  transition: background 0.15s ease;
}

.more-btn:hover {
  background: #e3e8f0;
}
</style>
